

import { useEffect, useRef, useContext } from 'react';
import { SoundContext } from '../Context/SoundContext';
import { useModal, MODAL_TYPES } from '../Context/ModalContext';

// Per-loop max volume (0.0 to 1.0)
const LOOP_MAX_VOLUMES = {
  filter: 10.9, // You can set above 1.0 for louder max
  pump: 3.4,
};

// Helper for smooth fade
function fadeGain(gainNode, to, duration = 0.25) {
  const now = gainNode.context.currentTime;
  gainNode.gain.cancelScheduledValues(now);
  gainNode.gain.setValueAtTime(gainNode.gain.value, now);
  gainNode.gain.linearRampToValueAtTime(to, now + duration);
}

const AudioPlayer = () => {
  const { filterVolume, pumpVolume, isMuted, ensureAudioContextResumed, filterBuffer, pumpBuffer, audioContext } = useContext(SoundContext);
  const { modal } = useModal();

  // Refs for audio context and nodes
  const filterSourceRef = useRef(null);
  const pumpSourceRef = useRef(null);
  const filterGainRef = useRef(null);
  const pumpGainRef = useRef(null);
  
  // Track if loops should be playing (used for tab visibility)
  const shouldBePlayingRef = useRef(false);

  // Persistent background loops: only start/stop when needed
  useEffect(() => {
    const ctx = audioContext?.current;
    if (!ctx || !filterBuffer?.current || !pumpBuffer?.current) return;

    // Helper to start a loop only if not already playing
    async function ensureLoop(buffer, gainRef, sourceRef, volume, loopType) {
      if (sourceRef.current) return; // already playing
      // Resume AudioContext before starting playback
      if (ensureAudioContextResumed) {
        await ensureAudioContextResumed();
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = ctx.createGain();
      // Use per-loop max volume
      const maxVol = LOOP_MAX_VOLUMES[loopType] ?? 1.0;
      gain.gain.value = (volume / 100) * maxVol;
      source.connect(gain).connect(ctx.destination);
      source.start(0);
      gainRef.current = gain;
      sourceRef.current = source;
    }

    // Helper to fade out and stop
    function stopLoop(gainRef, sourceRef) {
      if (gainRef.current && sourceRef.current) {
        fadeGain(gainRef.current, 0, 0.2);
        const src = sourceRef.current;
        setTimeout(() => {
          try { src.stop(); } catch {}
          src.disconnect();
        }, 200);
        sourceRef.current = null;
        gainRef.current = null;
      }
    }

    // Only play background loops if not muted AND (no modal open OR SoundSettings/Wisdom modal open)
    const isSoundSettingsOpen = modal.isOpen && (modal.type === 'SoundSettings' || modal.type === MODAL_TYPES.SOUND_SETTINGS);
    const isWisdomModalOpen = modal.isOpen && (modal.type === 'WisdomModal' || modal.type === MODAL_TYPES.WISDOM);
    const shouldPlay = (!modal.isOpen || isSoundSettingsOpen || isWisdomModalOpen) && !isMuted && !document.hidden;
    
    shouldBePlayingRef.current = shouldPlay;
    
    if (shouldPlay) {
      ensureLoop(filterBuffer.current, filterGainRef, filterSourceRef, filterVolume, 'filter');
      ensureLoop(pumpBuffer.current, pumpGainRef, pumpSourceRef, pumpVolume, 'pump');
    } else {
      stopLoop(filterGainRef, filterSourceRef);
      stopLoop(pumpGainRef, pumpSourceRef);
    }

    // Clean up on unmount
    return () => {
      stopLoop(filterGainRef, filterSourceRef);
      stopLoop(pumpGainRef, pumpSourceRef);
    };
    // eslint-disable-next-line
  }, [modal.isOpen, modal.type, isMuted, filterBuffer?.current, pumpBuffer?.current]);

  // Volume changes (fade smoothly)
  useEffect(() => {
    if (filterGainRef.current) {
      const maxVol = LOOP_MAX_VOLUMES['filter'] ?? 1.0;
      fadeGain(filterGainRef.current, isMuted ? 0 : (filterVolume / 100) * maxVol, 0.2);
    }
  }, [filterVolume, isMuted]);
  useEffect(() => {
    if (pumpGainRef.current) {
      const maxVol = LOOP_MAX_VOLUMES['pump'] ?? 1.0;
      fadeGain(pumpGainRef.current, isMuted ? 0 : (pumpVolume / 100) * maxVol, 0.2);
    }
  }, [pumpVolume, isMuted]);

  // Handle tab visibility changes - mute when tab is hidden, resume when visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab lost focus - fade out audio
        if (filterGainRef.current) fadeGain(filterGainRef.current, 0, 0.2);
        if (pumpGainRef.current) fadeGain(pumpGainRef.current, 0, 0.2);
      } else {
        // Tab regained focus - fade back in only if should be playing
        // (respects mute button and modal state)
        if (shouldBePlayingRef.current) {
          if (filterGainRef.current) {
            const maxVol = LOOP_MAX_VOLUMES['filter'] ?? 1.0;
            fadeGain(filterGainRef.current, (filterVolume / 100) * maxVol, 0.2);
          }
          if (pumpGainRef.current) {
            const maxVol = LOOP_MAX_VOLUMES['pump'] ?? 1.0;
            fadeGain(pumpGainRef.current, (pumpVolume / 100) * maxVol, 0.2);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [filterVolume, pumpVolume]);

  // No UI
  return null;
};

export default AudioPlayer;
